import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useCreateCarrierService, useUpdateCarrierService } from '@/api/domains/carrier-services';
import { useUpdateMarket } from '@/api/domains/markets';
import { useMe } from '@/api/domains/merchant-settings';
import type { AvailableMethod, CarrierServicePayload, Market } from '@/api/types/services';
import { Button } from '@/components/ui/button';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Form,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { resolveLocale, toIntlLocale } from '@/i18n';
import { applyApiErrors, toApiProblem } from '@/lib/errors';

import { NearbyPointsStepper } from './components/NearbyPointsStepper';
import {
  NEARBY_POINTS_DEFAULT,
  POINT_SEARCH_DISTANCE_MAX,
  POINT_SEARCH_DISTANCE_MIN,
  POINT_TYPE_BOTH,
  POINT_TYPES,
  WEIGHT_UNITS,
  methodConfigSchema,
  type MethodConfigFormValues,
} from './schemas';

function currencySymbol(currency: string | undefined, locale: string): string {
  if (!currency) return '';
  try {
    return (
      new Intl.NumberFormat(locale, { style: 'currency', currency })
        .formatToParts(0)
        .find((part) => part.type === 'currency')?.value ?? currency
    );
  } catch {
    return currency;
  }
}

type Props = {
  market: Market;
  method: AvailableMethod;
  /** Priority appended to the end of the market's configured list when creating. */
  nextDisplayPriority: number;
  onClose: () => void;
};

/**
 * Figma quick-setup panel: gray full-width strip aligned under the row's name
 * column. Row one: Shipping Cost / Free Shipping From / Max Weight, each with a
 * gray suffix chip (currency or unit). Row two: Status switch with Active label,
 * and the Nearby Points stepper (point methods only). Yellow Save bottom-right.
 */
export function MethodEditor({ market, method, nextDisplayPriority, onClose }: Props) {
  const { t, i18n } = useTranslation('markets');
  const tCommon = useTranslation('common').t;
  const locale = toIntlLocale(resolveLocale(i18n.language));
  const service = method.service;
  const create = useCreateCarrierService();
  const update = useUpdateCarrierService(service?.id ?? 0);
  const updateMarket = useUpdateMarket(market.id);
  const meQuery = useMe();
  const currency = service?.currency ?? meQuery.data?.merchant.shop_currency ?? undefined;
  const symbol = currencySymbol(currency, locale);

  const form = useForm<MethodConfigFormValues>({
    resolver: zodResolver(methodConfigSchema),
    defaultValues: {
      flat_rate_price: service?.flat_rate_price ?? 0,
      free_shipping_threshold: service?.free_shipping_threshold ?? null,
      max_weight: service?.max_weight ?? null,
      max_weight_unit: service?.max_weight_unit ?? 'KG',
      nearby_points_count: service?.nearby_points_count ?? NEARBY_POINTS_DEFAULT,
      point_type: service?.point_type ?? null,
      max_point_search_distance_km: market.max_point_search_distance_km ?? null,
      is_enabled: service?.is_enabled ?? true,
    },
  });

  const onSubmit = (values: MethodConfigFormValues) => {
    const payload: Partial<CarrierServicePayload> = {
      flat_rate_price: values.flat_rate_price,
      free_shipping_threshold: values.free_shipping_threshold ?? null,
      max_weight: values.max_weight ?? null,
      max_weight_unit: values.max_weight == null ? null : (values.max_weight_unit ?? 'KG'),
      is_enabled: values.is_enabled,
    };
    if (method.requires_point_selection) {
      payload.nearby_points_count = values.nearby_points_count ?? NEARBY_POINTS_DEFAULT;
      payload.point_type = values.point_type ?? null;
    }

    // The search distance is market-scoped (edited here because the picker only
    // exists on the point method) - it saves through the market endpoint, and only
    // when it actually changed.
    const savedDistance = market.max_point_search_distance_km ?? null;
    const nextDistance = method.requires_point_selection
      ? (values.max_point_search_distance_km ?? null)
      : savedDistance;

    const finish = () => {
      toast.success(service ? t('methods.updated') : t('methods.created'));
      onClose();
    };
    const handlers = {
      onSuccess: () => {
        if (nextDistance === savedDistance) {
          finish();
          return;
        }
        updateMarket.mutate(
          { max_point_search_distance_km: nextDistance },
          {
            onSuccess: finish,
            onError: (error: Error) => applyApiErrors(form, toApiProblem(error)),
          },
        );
      },
      onError: (error: Error) => applyApiErrors(form, toApiProblem(error)),
    };

    if (service) {
      update.mutate(payload, handlers);
    } else {
      create.mutate(
        {
          ...payload,
          market_id: market.id,
          method_key: method.method_key,
          display_priority: nextDisplayPriority,
          flat_rate_price: values.flat_rate_price,
        },
        handlers,
      );
    }
  };

  const isPending = create.isPending || update.isPending || updateMarket.isPending;

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-[30px] bg-[#f7f7f7] px-4 py-6 md:pl-[117px] md:pr-[37px]"
      >
        <div className="flex flex-wrap items-start gap-8">
          <FormField
            control={form.control}
            name="flat_rate_price"
            render={({ field }) => (
              <FormItem className="space-y-3">
                <FormLabel className="text-sm font-medium leading-none text-[#171717]">
                  {t('editor.shippingCost')}
                </FormLabel>
                <div className="flex items-center gap-[7px]">
                  <FormControl>
                    <Input
                      type="number"
                      min={0}
                      step={0.01}
                      className="w-[157px] bg-white"
                      value={Number.isNaN(field.value) ? '' : (field.value ?? '')}
                      onChange={(e) => field.onChange(e.target.valueAsNumber)}
                      disabled={isPending}
                    />
                  </FormControl>
                  <span className="flex h-9 w-[42px] items-center justify-center rounded-md border border-input bg-[#efefef] text-sm text-foreground">
                    {symbol}
                  </span>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="free_shipping_threshold"
            render={({ field }) => (
              <FormItem className="space-y-3">
                <FormLabel className="text-sm font-medium leading-none text-[#171717]">
                  {t('editor.freeShippingFrom')}
                </FormLabel>
                <div className="flex items-center gap-[7px]">
                  <FormControl>
                    <Input
                      type="number"
                      min={0}
                      step={0.01}
                      className="w-[157px] bg-white"
                      value={field.value ?? ''}
                      onChange={(e) =>
                        field.onChange(e.target.value === '' ? null : e.target.valueAsNumber)
                      }
                      disabled={isPending}
                    />
                  </FormControl>
                  <span className="flex h-9 w-[42px] items-center justify-center rounded-md border border-input bg-[#efefef] text-sm text-foreground">
                    {symbol}
                  </span>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="max_weight"
            render={({ field }) => (
              <FormItem className="space-y-3">
                <FormLabel className="text-sm font-medium leading-none text-[#171717]">
                  {t('editor.maxWeight')}
                </FormLabel>
                <div className="flex items-center gap-[7px]">
                  <FormControl>
                    <Input
                      type="number"
                      min={0}
                      step={0.1}
                      className="w-[71px] bg-white"
                      value={field.value ?? ''}
                      onChange={(e) =>
                        field.onChange(e.target.value === '' ? null : e.target.valueAsNumber)
                      }
                      disabled={isPending}
                    />
                  </FormControl>
                  <FormField
                    control={form.control}
                    name="max_weight_unit"
                    render={({ field: unitField }) => (
                      <select
                        aria-label={t('editor.maxWeightUnit')}
                        className="h-9 w-[42px] appearance-none rounded-md border border-input bg-[#efefef] text-center text-sm text-foreground focus-visible:outline-none"
                        value={unitField.value ?? 'KG'}
                        onChange={(e) => unitField.onChange(e.target.value)}
                        disabled={isPending}
                      >
                        {WEIGHT_UNITS.map((unit) => (
                          <option key={unit} value={unit}>
                            {t(`weightUnits.${unit}`)}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                </div>
                <FormMessage />
                {/* The unit select is a bare control inside this field's item, so the
                    schema refine error on max_weight_unit needs its own outlet. */}
                {form.formState.errors.max_weight_unit ? (
                  <p className="text-[0.8rem] font-medium text-destructive">
                    {form.formState.errors.max_weight_unit.message}
                  </p>
                ) : null}
              </FormItem>
            )}
          />
        </div>

        <div className="flex flex-wrap items-start gap-5">
          <FormField
            control={form.control}
            name="is_enabled"
            render={({ field }) => (
              <FormItem className="w-[132px] space-y-[15px]">
                <FormLabel className="text-sm font-medium leading-none text-[#171717]">
                  {t('editor.status')}
                </FormLabel>
                <div className="flex items-center gap-2.5">
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={isPending}
                    />
                  </FormControl>
                  <span className="text-sm font-medium text-[#171717]">
                    {field.value ? t('editor.active') : t('editor.inactive')}
                  </span>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          {method.requires_point_selection ? (
            <FormField
              control={form.control}
              name="nearby_points_count"
              render={({ field }) => (
                <FormItem className="w-[381px] max-w-full space-y-3">
                  <FormLabel className="text-sm font-medium leading-none text-[#171717]">
                    {t('editor.nearbyPoints')}
                  </FormLabel>
                  <p className="text-sm text-muted-foreground">{t('editor.nearbyPointsHint')}</p>
                  <FormControl>
                    <div>
                      <NearbyPointsStepper
                        value={field.value ?? NEARBY_POINTS_DEFAULT}
                        onChange={field.onChange}
                        disabled={isPending}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ) : null}
          {method.requires_point_selection ? (
            <FormField
              control={form.control}
              name="point_type"
              render={({ field }) => (
                <FormItem className="w-[240px] max-w-full space-y-3">
                  <FormLabel className="text-sm font-medium leading-none text-[#171717]">
                    {t('editor.pointType')}
                  </FormLabel>
                  <p className="text-sm text-muted-foreground">{t('editor.pointTypeHint')}</p>
                  <Select
                    value={field.value ?? POINT_TYPE_BOTH}
                    onValueChange={(value) =>
                      field.onChange(value === POINT_TYPE_BOTH ? null : value)
                    }
                    disabled={isPending}
                  >
                    <FormControl>
                      <SelectTrigger className="bg-white" aria-label={t('editor.pointType')}>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={POINT_TYPE_BOTH}>
                        {t('editor.pointTypeOptions.both')}
                      </SelectItem>
                      {POINT_TYPES.map((pointType) => (
                        <SelectItem key={pointType} value={pointType}>
                          {t(`editor.pointTypeOptions.${pointType}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          ) : null}
          {method.requires_point_selection ? (
            <FormField
              control={form.control}
              name="max_point_search_distance_km"
              render={({ field }) => (
                <FormItem className="w-[240px] max-w-full space-y-3">
                  <FormLabel className="text-sm font-medium leading-none text-[#171717]">
                    {t('editor.maxPointSearchDistance')}
                  </FormLabel>
                  <p className="text-sm text-muted-foreground">
                    {t('editor.maxPointSearchDistanceHint')}
                  </p>
                  <div className="flex items-center gap-[7px]">
                    <FormControl>
                      <Input
                        type="number"
                        min={POINT_SEARCH_DISTANCE_MIN}
                        max={POINT_SEARCH_DISTANCE_MAX}
                        className="w-[157px] bg-white"
                        placeholder={t('editor.maxPointSearchDistancePlaceholder')}
                        value={field.value ?? ''}
                        onChange={(e) =>
                          field.onChange(e.target.value === '' ? null : e.target.valueAsNumber)
                        }
                        disabled={isPending}
                      />
                    </FormControl>
                    <span className="flex h-9 w-[42px] items-center justify-center rounded-md border border-input bg-[#efefef] text-sm text-foreground">
                      km
                    </span>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          ) : null}
        </div>

        <div className="flex justify-end">
          <Button type="submit" className="w-20" disabled={isPending}>
            {isPending ? tCommon('actions.saving') : t('editor.save')}
          </Button>
        </div>
      </form>
    </Form>
  );
}
